/**
 * WGReferenceDocs Component - Working Group Reference Documents Management - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Comprehensive reference document management interface with search functionality, pagination,
 * and dual view modes (card/list). Features PDF modal integration with multi-modal support,
 * real-time search filtering, and paginated document browsing with responsive design.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of reference document objects
 * @param {string|number} props.data[].id - Unique document identifier
 * @param {string} props.data[].title - Document title
 * @param {string} props.data[].summary - Document summary/description
 * @param {string} props.data[].fileName - PDF file name/URL
 * @returns {JSX.Element} The WGReferenceDocs component
 * 
 * @example
 * <WGReferenceDocs data={referenceDocumentsArray} />
 */

import React, { useState, useMemo } from 'react';
import PDFModal from '../../modals/PDFModal';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css'; 


function WGReferenceDocs({ data }) {
  const [modals, setModals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6; // Number of items to show per page

  /**
   * Cleans text content by removing markdown and formatting artifacts
   * 
   * @param {string} text - Raw text to clean
   * @returns {string} Cleaned text without formatting artifacts
   */
  const cleanText = (text) => {
    return text
      .replace(/\*\*/g, '')
      .replace(/\"\"/g, '')
      .replace(/\'\'/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Filter and paginate data based on search query
  const filteredData = useMemo(() => {
    /**
     * Filters documents based on search query matching title or summary
     * 
     * @returns {Array} Filtered array of documents matching search criteria
     */
    return data.filter(doc => 
      cleanText(doc.title).toLowerCase().includes(searchQuery.toLowerCase()) ||
      cleanText(doc.summary).toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [data, searchQuery]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    /**
     * Creates paginated subset of filtered data for current page
     * 
     * @returns {Array} Array of documents for current page
     */
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  /**
   * Opens a PDF modal for document viewing
   * Manages modal stacking with z-index and positioning offsets
   * 
   * @param {string|number} id - Unique document identifier
   * @param {string} title - Document title for modal header
   * @param {string} pdfUrl - URL or filename for the PDF document
   */
  const openPDFModal = (id, title, pdfUrl) => {
    const existingModalIndex = modals.findIndex((modal) => modal.id === id);
    const maxZIndex = Math.max(...modals.map((modal) => modal.zIndex), 0) + 1;

    // Use ID-based URL instead of filename
    //const safeUrl = `${process.env.REACT_APP_API_URL}graph/getFileContentByListItemId/${id}`;

    if (existingModalIndex !== -1) {
      const updatedModals = [...modals];
      updatedModals[existingModalIndex].isOpen = true;
      updatedModals[existingModalIndex].zIndex = maxZIndex;
      setModals(updatedModals);
    } else {
      const newModal = {
        id,
        title,
        pdfUrl: pdfUrl, 
        isOpen: true,
        top: `${15 + (modals.length * 2)}%`,
        left: `${30 + (modals.length * 2)}%`,
        zIndex: maxZIndex,
      };
      setModals((prevModals) => [...prevModals, newModal]);
    }
  };

  /**
   * Closes a PDF modal by its unique identifier
   * 
   * @param {string|number} id - Unique identifier for the modal to close
   */
  const closePDFModal = (id) => {
    setModals((prevModals) =>
      prevModals.map((modal) =>
        modal.id === id ? { ...modal, isOpen: false } : modal
      )
    );
  };

  /**
   * Handles search input changes and resets pagination
   * 
   * @param {Event} e - Input change event
   */
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  /**
   * Handles pagination page changes
   * 
   * @param {number} page - Target page number
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div>
      <div className="search-box-wrapper">
        <div className="search-input-container">
          <svg 
            aria-hidden="true" 
            focusable="false" 
            data-prefix="fas" 
            data-icon="magnifying-glass" 
            className="svg-inline--fa fa-magnifying-glass search-icon" 
            role="img" 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 512 512"
          >
            <path 
              fill="currentColor" 
              d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={handleSearch}
            className="filtersearch"
          />
        </div>
      </div>

      <Tabs>
        <TabList>
          <Tab><i className="fa-solid fa-table-cells"></i> Card View</Tab>
          <Tab><i className="fa-solid fa-list"></i> List View</Tab>
        </TabList>

        <TabPanel>
          <section className="minutes-grid">
            {paginatedData.map((doc) => (
              <Tippy 
                key={doc.id}
                content={doc.summary}
                placement="right"
                interactive={true}
                appendTo={() => document.body}
                theme="light"
                duration={200}
                delay={[200, 0]}
              >
                <div className="minute-card">
                  <div 
                    className="minute-card-content"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPDFModal(doc.id, doc.title, doc.fileName);
                    }}
                  >
                    <h3 className="minute-title">{cleanText(doc.title)}</h3>
                    <p className="minute-summary">{cleanText(doc.summary)}</p>
                    <div className="pdf-icon">
                      <i className="fa-regular fa-file-pdf"></i>
                    </div>
                  </div>
                </div>
              </Tippy>
            ))}
          </section>
        </TabPanel>

        <TabPanel>
          <div className="minutes-list">
            {paginatedData.map((doc) => (
              <div key={doc.id} className="meeting-list-item">
                <div 
                  className="meeting-list-content"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPDFModal(doc.id, doc.title, doc.fileName);
                  }}
                >
                  <div className="meeting-list-header">
                    <h3 className="meeting-list-title">{cleanText(doc.title)}</h3>
                  </div>
                  <p className="meeting-list-summary">{cleanText(doc.summary)}</p>
                  <div className="pdf-icon">
                    <i className="fa-regular fa-file-pdf"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabPanel>
      </Tabs>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      )}

      {modals.map((modal) => (
        <PDFModal
          key={modal.id}
          title={modal.title}
          url={modal.pdfUrl}
          show={modal.isOpen}
          onClose={(e) => {
            e.stopPropagation();
            closePDFModal(modal.id);
          }}
          allowFullscreen={true}
          allowMove={true}
          showLinkinToolBar={true}
           />
      ))}
    </div>
  );
}

export default WGReferenceDocs;